import React from 'react';

/**
 * Review Needed Badge Component
 * Displays a warning badge indicating that a field needs review
 * Used for AI-generated drafts with missing or incomplete fields
 */
export const ReviewNeededBadge = ({ fieldName, missingFields = [] }) => {
  // Check if this field needs review
  const needsReview = missingFields && missingFields.includes(fieldName);
  
  if (!needsReview) return null;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs font-medium ml-2"
      title="This field was missing or incomplete in AI-generated content and needs review"
    >
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      Review Needed
    </span>
  );
};

/**
 * Helper function to check if a field needs review
 * Handles nested field paths (e.g., "seo.seo_title" -> checks "seo_title")
 */
export const fieldNeedsReview = (fieldName, missingFields = []) => {
  if (!missingFields || !Array.isArray(missingFields)) return false;
  
  // Direct match
  if (missingFields.includes(fieldName)) return true;
  
  // Check for nested paths (e.g., "seo.seo_title" matches "seo_title")
  const nestedMatch = missingFields.find(mf => {
    if (typeof mf !== 'string') return false;
    const parts = mf.split('.');
    return parts[parts.length - 1] === fieldName;
  });
  
  return !!nestedMatch;
};

/**
 * Helper function to map transformer field names to form field names
 */
export const mapTransformerFieldToFormField = (transformerField, entityType) => {
  const fieldMappings = {
    university: {
      'name': 'name',
      'short_description': 'short_description',
      'about_university': 'about',
      'institution_type': 'institution_type',
      'country': 'country',
      'state': 'state',
      'city': 'city',
      'program_levels_offered': 'levels_offered',
      'academic_focus': 'fields_of_study',
      'seo.seo_title': 'seo_title',
      'seo.seo_description': 'seo_description',
    },
    program: {
      'program_name': 'program_name',
      'short_description': 'short_description',
      'level_of_study': 'levels_of_study',
      'field_of_study': 'fields_of_study',
      'fees.local_students.amount': 'estimated_fee_local',
      'fees.international_students.amount': 'estimated_fee_international',
      'program_modules': 'course_modules',
      'entry_requirements': 'eligibility_requirements',
      'seo.seo_title': 'seo_title',
      'seo.seo_description': 'seo_description',
    },
    scholarship: {
      'scholarship_name': 'name',
      'short_description': 'short_description',
      'provider_name': 'provider',
      'provider_type': 'provider_type',
      'applicable_country': 'applicable_country',
      'scholarship_value': 'value_amount',
      'eligibility_criteria': 'eligibility_criteria',
      'application_process': 'application_process',
      'seo.seo_title': 'seo_title',
      'seo.seo_description': 'seo_description',
    }
  };
  
  const mapping = fieldMappings[entityType] || {};
  return mapping[transformerField] || transformerField;
};
